/**
 * 错误归一（骨架，#89098）
 *
 * CloudBase 侧存在**三套**错误体，直接把它们暴露给生成应用会让每个调用点都要
 * 分别判形状：
 *
 *  1. auth（OAuth2）      `{ error, error_description, error_uri, details }`
 *  2. PostgREST（数据）    `{ message, details, hint, code }`
 *  3. functions / 存储     `{ code, message, requestId }`
 *
 * 这里统一成一个**判别联合**，让调用方能 `switch (error.kind)` 且被 TS 穷尽检查 ——
 * 这一点刻意比 Supabase 做得更严：它只给一个笼统 error 类 + 字符串 code，无法穷尽。
 */

/**
 * 归一后的错误类别。
 *
 * `kind` 是稳定的编程契约，`message` 只供展示 —— 不要去 match message 文本。
 */
export type CloudErrorKind =
    /** 未登录 / token 失效。TCB 返 `invalid_grant` 时应清空本地凭据（§5.3）。 */
    | 'unauthenticated'
    /** 已登录但无权限，通常是 RLS 拒绝。 */
    | 'permission-denied'
    | 'not-found'
    /** 参数或请求体不合法。 */
    | 'invalid-request'
    /** 一期未放行的端点（captcha / device code 等，§5.4）。 */
    | 'unimplemented'
    /** 额度耗尽或环境已销毁，读写必然失败（§11.3）。 */
    | 'credits-exhausted'
    | 'rate-limited'
    /** 网关或上游故障。 */
    | 'backend-unavailable'
    /** fetch 层失败（离线、DNS、CORS、超时）。 */
    | 'network'
    /** 无法归类 —— 保留原始信息，不要吞掉。 */
    | 'unknown';

/** 归一后的错误。四个模块只抛/返回这一种形状。 */
export interface CloudError {
    kind: CloudErrorKind;
    /** 面向用户的可读信息。 */
    message: string;
    /** HTTP 状态码；网络层失败时为 0。 */
    status: number;
    /** 上游原始错误码（PostgREST 的 `42501`、OAuth2 的 `invalid_grant` 等），便于排查。 */
    code?: string;
    /** 上游原始错误体，**原样保留** —— 归一不等于丢信息。 */
    cause?: unknown;
}

/**
 * 成功/失败信封。
 *
 * 必须是**判别联合**（两个成员的 union），不能写成 `{ data?: T; error?: E }` ——
 * 后者会让 `if (error) return` 之后的 `data` 收窄失效，这是 Supabase 该设计
 * 最容易被抄错的地方。
 */
export type CloudResult<T> =
    | { data: T; error: null }
    | { data: null; error: CloudError };

/** 该错误是否意味着应清空本地凭据并要求重新登录。 */
export function shouldClearCredentials(error: CloudError): boolean {
    return error.kind === 'unauthenticated' || error.code === 'invalid_grant';
}
